import React, { FC, useContext, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Lawyer } from '../../../entity/entity.types';
import { LawyerList } from '../../../entity/entity-lists/LawyerList';
import { doctrineAnalytics } from '../../../utils/analyticsUtils';
import { AllLawFirmLawyersModal } from '../../../modals/entity-lists/AllLawFirmLawyersModal';
import {
  EntityBlock,
  EntityBlockTitle,
} from '../../../entity/components/EntityBlockComponents';
import { SeeMore } from '../../../components/see-more/SeeMore';
import { numberWithSpaces } from '../../../utils/math';
import { useRestrictedModal } from '../../../components/modal/useRestrictedModal';
import { useModal } from '../../../components/modal/useModal';
import { PageNavContext } from '../../../components/page-nav/PageNavContext';
import { EntityOutlineTitle } from '../../../entity/components/EntityOutlineTitle';
import { BigLineSeparator } from '../../../components/separator/LineSeparator';
import { useUser } from '../../../user/useUser';

type Props = {
  lawyers: Array<Lawyer>;
};

export const LawFirmLawyers: FC<Props> = ({ lawyers }) => {
  const id = 'law-firm-lawyers';
  const limit = 5;
  const getEntityTitle = (lawyer: Lawyer) => lawyer.name;
  const getEntitySubtitle = (lawyer: Lawyer) => `${
    lawyer.barreau === 'Avocat aux conseils'
      ? 'Ordre des Avocats aux conseils'
      : `Barreau : ${lawyer.barreau}`
  }
      ${
        lawyer.sermentDate
          ? ` · Prestation de serment : ${lawyer.sermentDate}`
          : ''
      }`;
  const { accessPremium } = useUser();
  const { openRestrictedModal } = useRestrictedModal();
  const { createNav } = useContext(PageNavContext);

  const { openModal } = useModal((modalProps) => (
    <AllLawFirmLawyersModal
      lawyers={lawyers}
      source="law-firm"
      getEntityTitle={getEntityTitle}
      getEntitySubtitle={getEntitySubtitle}
      {...modalProps}
    />
  ));

  useEffect(() => {
    createNav(id);
  }, []);

  const handleSeeMoreClick = () => {
    if (accessPremium) {
      openModal();
    } else {
      openRestrictedModal({ source: 'office_see_more_lawyers' });
    }
    doctrineAnalytics('Office SeeMoreLawyers');
  };

  const lawyerShortList = lawyers.slice(0, limit);
  const hasMore = lawyers.length > limit;

  return (
    <>
      <EntityOutlineTitle id={id}>
        <FormattedMessage id="law-firm/page/LawFirmLawyers.tsx/1" />
      </EntityOutlineTitle>
      <EntityBlock>
        <EntityBlockTitle>
          <FormattedMessage id="law-firm/page/LawFirmLawyers.tsx/2" />
        </EntityBlockTitle>
        <BigLineSeparator />
        <LawyerList
          lawyers={lawyerShortList}
          source="law-firm"
          getEntityTitle={getEntityTitle}
          getEntitySubtitle={getEntitySubtitle}
        />
        {hasMore && (
          <SeeMore onClick={handleSeeMoreClick}>
            <FormattedMessage
              id="law-firm/page/LawFirmLawyers.tsx/3"
              values={{ count: numberWithSpaces(lawyers.length) }}
            />
          </SeeMore>
        )}
      </EntityBlock>
    </>
  );
};
